import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Brain, CalendarDays, Loader2, Radar, Sparkles, Target, Watch } from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { fmtNum, timeAgo } from "@/lib/format";

const KIND = {
  trade_of_day: { label: "Trade of the Day", icon: Target, tone: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20" },
  daily_brief: { label: "Daily Brief", icon: BookOpen, tone: "text-blue-300 bg-blue-500/10 border-blue-500/20" },
  watchlist_change: { label: "Watchlist", icon: Watch, tone: "text-amber-300 bg-amber-500/10 border-amber-500/20" },
  live_signal: { label: "Live Signal", icon: Radar, tone: "text-violet-300 bg-violet-500/10 border-violet-500/20" },
};

function kindInfo(kind) {
  return KIND[kind] || { label: kind?.replaceAll("_", " ") || "Item", icon: Sparkles, tone: "text-zinc-300 bg-zinc-500/10 border-zinc-700" };
}

function statusTone(status) {
  if (status === "live" || status === "fresh") return "text-emerald-300 bg-emerald-500/10";
  if (status === "stale" || status === "ended" || status === "closed") return "text-amber-300 bg-amber-500/10";
  return "text-zinc-400 bg-zinc-800";
}

function TodayCard({ item, rank }) {
  const info = kindInfo(item.kind);
  const Icon = info.icon;
  const body = (
    <article className="group rounded-md border border-zinc-800 bg-zinc-950/60 p-4 hover:border-zinc-700 transition-colors" data-testid={`today-item-${rank}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono-num text-xs text-zinc-500">#{rank}</span>
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${info.tone}`}>
              <Icon size={11} /> {info.label}
            </span>
            {item.status && (
              <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${statusTone(item.status)}`}>{item.status}</span>
            )}
          </div>
          <h2 className="mt-3 font-heading text-lg font-semibold text-zinc-100 leading-snug">{item.title}</h2>
          {item.subtitle && <p className="mt-1 text-xs text-zinc-500">{item.subtitle}</p>}
          {item.reason && <p className="mt-3 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap line-clamp-5">{item.reason}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-zinc-500">
            {item.score != null && <span>score {fmtNum(item.score, 1)}</span>}
            {item.meta?.detected_at && <span>detected {timeAgo(item.meta.detected_at)}</span>}
            {item.meta?.generated_at && <span>generated {timeAgo(item.meta.generated_at)}</span>}
          </div>
        </div>
        {item.href && (
          <div className="shrink-0 text-xs text-zinc-400 group-hover:text-zinc-100 inline-flex items-center gap-1">
            Open <ArrowRight size={12} />
          </div>
        )}
      </div>
    </article>
  );
  return item.href ? <Link to={item.href}>{body}</Link> : body;
}

export default function Today() {
  React.useEffect(() => {
    api.post("/onboarding/progress/visited_today").catch(() => {});
  }, []);
  React.useEffect(() => { track("first_investigation"); }, []);
  const { data, isLoading, error } = useQuery({
    queryKey: ["today-guidance"],
    queryFn: async () => (await api.get("/intel/today")).data,
    refetchInterval: 90_000,
  });
  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="today-page">
      <header className="rounded-md border border-zinc-800 bg-gradient-to-br from-zinc-950 via-zinc-950 to-zinc-900 p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              <CalendarDays size={13} /> Today
            </div>
            <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold text-zinc-100 tracking-tight">
              What deserves your attention now.
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-zinc-400">
              One daily workflow built from the brief, Trade of the Day, watchlist changes, and live consensus signals.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:min-w-[420px]">
            <Metric label="Items" value={data?.counts?.items} />
            <Metric label="Signals" value={data?.counts?.live_signals_considered} />
            <Metric label="Watchlist" value={data?.counts?.watchlist_changes} />
            <Metric label="Updated" value={data?.generated_at ? timeAgo(data.generated_at) : "—"} text />
          </div>
        </div>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="space-y-3">
          {isLoading && (
            <div className="rounded-md border border-zinc-800 p-8 text-center text-sm text-zinc-500">
              <Loader2 size={16} className="mx-auto mb-2 animate-spin" /> Building today’s workflow…
            </div>
          )}
          {error && (
            <div className="rounded-md border border-red-900/60 bg-red-950/20 p-4 text-sm text-red-200">
              Today guidance failed to load.
            </div>
          )}
          {!isLoading && !error && items.length === 0 && (
            <div className="rounded-md border border-zinc-800 p-8 text-center text-sm text-zinc-500">
              No priority items right now. Check back after the next sync or add active markets to a watchlist.
            </div>
          )}
          {items.map((item, i) => <TodayCard key={item.id || i} item={item} rank={i + 1} />)}
        </div>

        <aside className="space-y-3">
          <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-100"><Brain size={14} /> Workflow</div>
            <ol className="mt-3 space-y-2 text-xs text-zinc-400 list-decimal list-inside">
              <li>Start with the top item.</li>
              <li>Open the linked market, signal, watchlist, or brief.</li>
              <li>Use Chat for follow-up questions if evidence is unclear.</li>
              <li>Watch, dismiss, or save context before moving on.</li>
            </ol>
          </div>
          <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-4 text-xs text-zinc-500 leading-relaxed">
            MarketTrace is read-only decision support. This page prioritizes what to investigate; it does not place trades or tell you to buy or sell.
          </div>
        </aside>
      </section>
    </div>
  );
}

function Metric({ label, value, text = false }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950/70 p-3">
      <div className="text-[9px] uppercase tracking-widest text-zinc-600">{label}</div>
      <div className="mt-1 font-mono-num text-sm text-zinc-100">{text ? (value || "—") : fmtNum(value)}</div>
    </div>
  );
}
